import { useMediaQuery, useTheme } from '@mui/material';
import type {
  GridColumnVisibilityModel,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCancelDeviceSignal } from '~/api/device-signals/cancel';
import { useRunDeviceSignal } from '~/api/device-signals/run';
import { ScrollableDataGrid } from '~/components/data-grid';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { EmptyState } from '~/components/empty-state';
import { ListCheckbox } from '~/components/list-row';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useOrderByParams } from '~/hooks/order-by';
import DeviceSignalsIllustration from '~/images/illustrations/device-signals.svg';
import { cancelPrompt, runPrompt } from '../../lib';
import type { NetworkDeviceSignals__DeviceSignal as DeviceSignal } from '../../queries/list.generated';
import { useColumns } from '../lib';

const NoRowsOverlay = () => {
  const { t } = useTranslation();

  return (
    <EmptyState
      illustration={DeviceSignalsIllustration}
      description={t('pages.device_signals.empty.description')}
      header={t('pages.device_signals.empty.header')}
    />
  );
};

export interface TableProps {
  deviceSignals: readonly DeviceSignal[];
  loading: boolean;
  onCheck: Dispatch<SetStateAction<GridRowSelectionModel>>;
  selectedIds: GridRowSelectionModel;
}

export const Table = ({ deviceSignals, loading, onCheck, selectedIds }: TableProps) => {
  const { t } = useTranslation();
  const [confirmCancel, confirmCancelProps] = useConfirmDialog();
  const [confirmRun, confirmRunProps] = useConfirmDialog();

  const [selectedSignal, setSelectedSignal] = useState<DeviceSignal | null>(null);
  const navigate = useNavigate();

  const [cancelDeviceSignal] = useCancelDeviceSignal();
  const [runDeviceSignal] = useRunDeviceSignal();

  const handleCancelClick = useCallback(
    async (signal: DeviceSignal) => {
      setSelectedSignal(signal);
      if (!(await confirmCancel())) return;
      await cancelDeviceSignal({
        variables: { input: { id: signal.id } },
      });
    },
    [cancelDeviceSignal, confirmCancel],
  );

  const handleRunClick = useCallback(
    async (signal: DeviceSignal) => {
      setSelectedSignal(signal);
      if (!(await confirmRun())) return;
      await runDeviceSignal({
        variables: { input: { id: signal.id } },
      });
    },
    [confirmRun, runDeviceSignal],
  );

  const columns = useColumns(handleCancelClick, handleRunClick);
  const [deviceSignalColumns, setDeviceSignalColumns] = useState<GridColumnVisibilityModel>({});

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isLargeAndDown = useMediaQuery(theme.breakpoints.down('lg'));
  const isXLargeAndDown = useMediaQuery(theme.breakpoints.down('xl'));

  const { setOrderByParams, sortModel } = useOrderByParams();

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      setOrderByParams(sortModel.at(0) ?? null);
    },
    [setOrderByParams],
  );

  useEffect(() => {
    setDeviceSignalColumns((x) => ({
      ...x,
      kind: !isXLargeAndDown,
      action: !isXLargeAndDown,
      actionTarget: !isXLargeAndDown,
      target: !isXLargeAndDown,
      schedule: !isXLargeAndDown,
      devices: !isLargeAndDown,
      eventType: !isLargeAndDown,
      event: !isLargeAndDown,
      status: !isLargeAndDown,
    }));
  }, [isLargeAndDown, isSmallAndDown, isXLargeAndDown]);

  return (
    <>
      <ScrollableDataGrid
        checkboxSelection={!isSmallAndDown}
        columns={columns}
        columnVisibilityModel={deviceSignalColumns}
        disableRowSelectionOnClick
        loading={loading}
        onColumnVisibilityModelChange={setDeviceSignalColumns}
        onRowSelectionModelChange={onCheck}
        onRowClick={({ row }: GridRowParams<DeviceSignal>) => navigate(String(row.id))}
        rows={deviceSignals}
        rowSelectionModel={selectedIds}
        slots={{
          baseCheckbox: ListCheckbox,
          noRowsOverlay: NoRowsOverlay,
        }}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
      />
      {selectedSignal && (
        <>
          <ConfirmDialog
            {...confirmCancelProps}
            confirm={t('pages.device_signals.list.dialogs.cancel.confirm')}
            deleteConfirm={false}
            prompt={cancelPrompt(selectedSignal)}
            title={t('pages.device_signals.list.dialogs.cancel.title')}
          />

          <ConfirmDialog
            {...confirmRunProps}
            confirm={t('pages.device_signals.list.dialogs.run.confirm')}
            deleteConfirm={false}
            prompt={runPrompt(selectedSignal)}
            title={t('pages.device_signals.list.dialogs.run.title')}
          />
        </>
      )}
    </>
  );
};
